import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { type SkillMetadataMap } from '../../types';
import { parseSkillNode } from '../../utils/parse-skill';
import { useRovoSkillsConfig, useRovoSkillsInvalidation } from '../rovo-skills-config-provider';

import type {
	skillMetadataProviderQuery,
	skillMetadataProviderQuery$data,
} from './__generated__/skillMetadataProviderQuery.graphql';

/**
 * Transforms the GraphQL response from the skill metadata query into a SkillMetadataMap.
 * The map is keyed by skill slug (extracted from slashCommand, e.g., "/jira-issues" -> "jira-issues").
 *
 * @param skillsResponse - The response from the graphIntegration_componentDirectoryItems query
 * @returns A map of skill slugs to skill metadata, or an empty object if the response is invalid
 */
export function transformSkillMetadataResponse(
	skillsResponse?: skillMetadataProviderQuery$data['graphIntegration_componentDirectoryItems'],
): SkillMetadataMap {
	if (!skillsResponse?.edges) {
		return {};
	}

	const metadataMap: SkillMetadataMap = {};

	skillsResponse.edges.forEach((edge) => {
		const node = edge?.node;
		if (!node || node.__typename !== 'GraphIntegrationSkillDirectoryItem') {
			return;
		}

		const skill = parseSkillNode(node);
		if (!skill || !skill.slug) {
			return;
		}

		metadataMap[skill.slug] = {
			id: skill.id,
			name: skill.name,
			description: skill.description ?? '',
			iconName: skill.iconName,
			product: skill.product,
			productIconUrl: skill.productIconUrl,
			color: skill.color,
		};
	});

	return metadataMap;
}

/**
 * Context value provided by SkillMetadataProvider
 */
type SkillMetadataContextValue = {
	skillMetadataMap: SkillMetadataMap;
};

/**
 * Context for skill metadata
 */
const SkillMetadataContext = createContext<SkillMetadataContextValue | undefined>(undefined);

/**
 * Hook to access skill metadata context.
 * Returns undefined if used outside of SkillMetadataProvider.
 * Useful for components that can work with or without skill metadata.
 *
 * @returns The skill metadata context value or undefined
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const skillMetadata = useSkillMetadata();
 *   const metadata = skillMetadata?.skillMetadataMap['jira-issues'];
 *   // Use metadata if available, fallback otherwise
 * }
 * ```
 */
export function useSkillMetadata(): SkillMetadataContextValue | undefined {
	return useContext(SkillMetadataContext);
}

type SkillMetadataProviderProps = {
	/**
	 * The Atlassian site ID for the current context.
	 * Required for fetching skill metadata from the backend.
	 */
	siteId: string;
	children: ReactNode;
	/**
	 * Optional tags configuration for conversation actions and skills.
	 *
	 * If provided as a prop, this will be used directly.
	 * If not provided, the provider will attempt to read tags from RovoSkillsConfigProvider context.
	 * If neither is available, no skill metadata will be fetched.
	 */
	tags?: {
		/**
		 * Tags identifying sets of skills registered in the backend.
		 */
		skillRegistryTags?: string[];
	};
	/**
	 * Optional flag to force fetch skill metadata even if no tags are provided. This repeats the query that the editor extension uses and should re-use its cache.
	 */
	fetchWithoutTags?: boolean;
};

/**
 * Provider component that fetches skill metadata from the backend and provides it via context.
 * This component colocates the GraphQL query with the data transformation logic
 * to satisfy ESLint relay rules (no-wrapping-hooks).
 *
 * This provider can work in two modes:
 *
 * **Mode 1: With RovoSkillsConfigProvider (recommended for shared config)**
 * The provider reads tags from RovoSkillsConfigProvider context. This is useful when multiple
 * components need access to the same configuration.
 * ```tsx
 * <RovoSkillsConfigProvider config={{ tags }}>
 *   <SkillMetadataProvider siteId={cloudId}>
 *     <MyComponent />
 *   </SkillMetadataProvider>
 * </RovoSkillsConfigProvider>
 * ```
 *
 * **Mode 2: Standalone (with tags prop)**
 * The provider can work independently by passing tags directly as a prop.
 * This is useful when RovoSkillsConfigProvider is not needed or available.
 * ```tsx
 * <SkillMetadataProvider siteId={cloudId} tags={tags}>
 *   <MyComponent />
 * </SkillMetadataProvider>
 * ```
 *
 * **Priority:** If both prop and context provide tags, the prop takes precedence.
 */
export function SkillMetadataProvider({
	children,
	siteId,
	tags: tagsProp,
	fetchWithoutTags = false,
}: SkillMetadataProviderProps): ReactNode {
	const config = useRovoSkillsConfig();
	const { skillsRevision } = useRovoSkillsInvalidation();
	// Use tags from prop if provided, otherwise fallback to context
	const tags = tagsProp ?? config?.tags;

	const shouldFetch =
		(!!tags?.skillRegistryTags && tags.skillRegistryTags.length > 0) || fetchWithoutTags;

	const skillsResponse = useLazyLoadQuery<skillMetadataProviderQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by transformSkillMetadataResponse in this file */
		graphql`
			query skillMetadataProviderQuery(
				$first: Int!
				$relevantFor: [GraphIntegrationDirectoryItemType!]!
				$cloudId: ID!
				$surface: GraphIntegrationSurface
				$shouldSkip: Boolean!
				$tags: [String!]
			) {
				graphIntegration_componentDirectoryItems(
					first: $first
					relevantFor: $relevantFor
					cloudId: $cloudId
					surface: $surface
					tags: $tags
				) @optIn(to: "GraphIntegrationItemsQuery") @skip(if: $shouldSkip) {
					edges {
						node {
							__typename
							... on GraphIntegrationSkillDirectoryItem {
								id
								slashCommand
								name
								description
								iconUrl
								integrationKey {
									displayName
									iconUrl
								}
								color
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			first: 100,
			relevantFor: ['SKILL'],
			cloudId: siteId,
			surface: 'ROVO',
			shouldSkip: !shouldFetch,
			tags: fetchWithoutTags ? undefined : (tags?.skillRegistryTags ?? []),
		},
		{
			fetchKey: skillsRevision,
			fetchPolicy: skillsRevision === 0 ? 'store-or-network' : 'network-only',
		},
	)?.graphIntegration_componentDirectoryItems;

	const skillMetadataMap = useMemo(() => {
		if (!shouldFetch) {
			return {};
		}
		return transformSkillMetadataResponse(skillsResponse);
	}, [shouldFetch, skillsResponse]);

	const value = useMemo(
		() => ({
			skillMetadataMap,
		}),
		[skillMetadataMap],
	);

	return <SkillMetadataContext.Provider value={value}>{children}</SkillMetadataContext.Provider>;
}
